import type { StringFilterColumnCondition } from "@/nzds/data-grid/string-filter-column-url"
import {
  readStringFilterColumn,
  writeStringFilterColumn,
} from "@/nzds/data-grid/string-filter-column-url"
import {
  gridUrlValuesKey,
  withGridUrlUpdates,
} from "@/nzds/data-grid/use-grid-url-state"

import { type GridFilterModel, GridLogicOperator } from "@mui/x-data-grid-pro"

export const ADMIN_METERS_GRID_URL_KEYS = [
  "logic",
  "meter_code",
  "meter_code_op",
  "meter_provider",
  "meter_provider_op",
  "site_name",
  "site_name_op",
] as const

type AdminMetersFilterField = "code" | "provider" | "siteName"
type AdminMetersFilterLogic = "and" | "or"

interface AdminMetersGridUrlState {
  code: StringFilterColumnCondition | null
  logic: AdminMetersFilterLogic
  provider: StringFilterColumnCondition | null
  siteName: StringFilterColumnCondition | null
}

interface AdminMetersAppliedFilters {
  code: { operator: string; value?: string } | null
  logic: AdminMetersFilterLogic
  provider: { operator: string; value?: string } | null
  siteName: { operator: string; value?: string } | null
}

interface FilterCriterion {
  field: string
  operator: string
  value?: string
}

const FILTER_FIELDS = ["code", "siteName", "provider"] as const

const FILTER_ITEM_IDS: Record<AdminMetersFilterField, string> = {
  code: "admin-meters-code",
  provider: "admin-meters-provider",
  siteName: "admin-meters-site-name",
}

const readLogic = (
  logicRaw: string | null | undefined
): AdminMetersFilterLogic => (logicRaw === "or" ? "or" : "and")

const getConditionForField = (
  criteria: readonly FilterCriterion[],
  field: string
): { operator: string; value?: string } | null => {
  const match = criteria.find((item) => item.field === field)
  if (!match) {
    return null
  }

  if (match.value === undefined) {
    return { operator: match.operator }
  }

  return { operator: match.operator, value: match.value }
}

const toFilterItem = (
  field: AdminMetersFilterField,
  condition: StringFilterColumnCondition
) => ({
  field,
  id: FILTER_ITEM_IDS[field],
  operator: condition.operator,
  ...(condition.value !== undefined ? { value: condition.value } : {}),
})

export const readAdminMetersGridUrl = (
  values: Record<string, string | null>
): AdminMetersGridUrlState => ({
  code: readStringFilterColumn(values.meter_code_op, values.meter_code),
  logic: readLogic(values.logic),
  provider: readStringFilterColumn(
    values.meter_provider_op,
    values.meter_provider
  ),
  siteName: readStringFilterColumn(values.site_name_op, values.site_name),
})

const writeAdminMetersGridUrl = (
  input: AdminMetersAppliedFilters
): Record<string, string | null> => {
  const code = writeStringFilterColumn(input.code)
  const siteName = writeStringFilterColumn(input.siteName)
  const provider = writeStringFilterColumn(input.provider)
  const appliedCount = [code, siteName, provider].filter(
    (column) => column.operator != null
  ).length

  return {
    logic: appliedCount >= 2 && input.logic === "or" ? "or" : null,
    meter_code: code.value,
    meter_code_op: code.operator,
    meter_provider: provider.value,
    meter_provider_op: provider.operator,
    site_name: siteName.value,
    site_name_op: siteName.operator,
  }
}

export const toAdminMetersFilterModel = (
  state: AdminMetersGridUrlState
): GridFilterModel => ({
  items: FILTER_FIELDS.flatMap((field) => {
    const condition = state[field]
    return condition ? [toFilterItem(field, condition)] : []
  }),
  logicOperator:
    state.logic === "or" ? GridLogicOperator.Or : GridLogicOperator.And,
})

export const getAppliedAdminMetersFilters = (
  criteria: readonly FilterCriterion[],
  logicOperator: string
): AdminMetersAppliedFilters => ({
  code: getConditionForField(criteria, "code"),
  logic: readLogic(logicOperator),
  provider: getConditionForField(criteria, "provider"),
  siteName: getConditionForField(criteria, "siteName"),
})

/**
 * Next replace for the owned filter keys. Load does not rewrite, so an unknown
 * match stays until the applied filters change. A change that is not already
 * page 1 includes `page=1` in the same replace.
 */
export const getNextAdminMetersGridUrlUpdate = ({
  applied,
  page,
  signatureChanged,
  values,
}: {
  applied: AdminMetersAppliedFilters
  page: number
  signatureChanged: boolean
  values: Record<string, string | null>
}): Record<string, string | null> | null => {
  if (!signatureChanged) {
    return null
  }

  const updates = writeAdminMetersGridUrl(applied)
  const filtersUnchanged =
    gridUrlValuesKey(
      ADMIN_METERS_GRID_URL_KEYS,
      withGridUrlUpdates(values, updates)
    ) === gridUrlValuesKey(ADMIN_METERS_GRID_URL_KEYS, values)

  if (filtersUnchanged && page === 1) {
    return null
  }

  return page === 1 ? updates : { ...updates, page: "1" }
}
